import { useEffect, useMemo, useRef, useState } from 'react';
import { getSearchedGames } from '@/features/games/internal/helpers/browser-clients/get-searched-games';
import type { GameInfo } from '@/features/games/shared/types/game-info';
import { useDebouncedValue } from '@ecom/helper-react-hooks';
import { useCurrency } from '@/features/shared/hooks/use-currency';
import {
  deriveSearchOutput,
  type FetchState,
  type GamesSearchError,
  type GamesSearchStatus,
} from '@/features/games/shared/helpers/derive-search-output';

export type { GamesSearchError, GamesSearchStatus };

const DEFAULT_MIN_GAMES_SEARCH_TERM_LENGTH = 3;
const DEFAULT_DEBOUNCE_MS = 500;

type UseGamesSearchOptions = {
  searchTerm: string;
  maxResults?: number;
  debounceMs?: number;
  minSearchTermLength?: number;
};

type UseGamesSearchResult = {
  status: GamesSearchStatus;
  isLoading: boolean;
  error: GamesSearchError | null;
  results: GameInfo[];
  displayedResults: GameInfo[];
  hasMoreResults: boolean;
  canSearch: boolean;
};

export const useGamesSearch = ({
  searchTerm,
  maxResults,
  debounceMs = DEFAULT_DEBOUNCE_MS,
  minSearchTermLength = DEFAULT_MIN_GAMES_SEARCH_TERM_LENGTH,
}: UseGamesSearchOptions): UseGamesSearchResult => {
  const [fetchState, setFetchState] = useState<FetchState>({ kind: 'idle' });
  const debouncedSearchTerm = useDebouncedValue(searchTerm, debounceMs);
  const abortControllerRef = useRef<AbortController | null>(null);

  const selectedCurrency = useCurrency();

  const trimmedSearchTerm = debouncedSearchTerm.trim();
  const canSearch = trimmedSearchTerm.length >= minSearchTermLength;

  useEffect(() => {
    if (!canSearch) {
      abortControllerRef.current?.abort();
      abortControllerRef.current = null;
      return;
    }

    const controller = new AbortController();
    abortControllerRef.current?.abort();
    abortControllerRef.current = controller;

    const fetchLimit = typeof maxResults === 'number' ? maxResults + 1 : undefined;

    const fetchData = async () => {
      try {
        setFetchState({ kind: 'loading', term: trimmedSearchTerm });

        const response = await getSearchedGames(trimmedSearchTerm, {
          limit: fetchLimit,
          signal: controller.signal,
        });

        if (controller.signal.aborted) {
          return;
        }

        setFetchState({ kind: 'success', term: trimmedSearchTerm, games: response.games });
      } catch (caughtError) {
        if (controller.signal.aborted) {
          return;
        }

        if (caughtError instanceof DOMException && caughtError.name === 'AbortError') {
          return;
        }

        // FetchGamesError and any other non-abort failure surface the same way
        setFetchState({ kind: 'error', term: trimmedSearchTerm });
      } finally {
        if (abortControllerRef.current === controller) {
          abortControllerRef.current = null;
        }
      }
    };

    void fetchData();

    return () => {
      controller.abort();
    };
  }, [canSearch, trimmedSearchTerm, maxResults, minSearchTermLength, selectedCurrency]);

  const { status, error, results } = deriveSearchOutput(fetchState, canSearch, trimmedSearchTerm);

  const displayedResults = useMemo(() => {
    if (typeof maxResults === 'number') {
      return results.slice(0, maxResults);
    }

    return results;
  }, [results, maxResults]);

  const hasMoreResults = useMemo(() => {
    if (typeof maxResults !== 'number') {
      return false;
    }

    return results.length > maxResults;
  }, [results, maxResults]);

  return {
    status,
    isLoading: status === 'loading',
    error,
    results,
    displayedResults,
    hasMoreResults,
    canSearch,
  };
};
